import { utmFromQuery, utmQuery } from "@fusiqo/vozqi-shared";

import { captureAffiliateCode } from "./lib/affiliate";
import { captureOrigin } from "./lib/origin";
import { detectPlatform } from "./lib/platform";
import { revealStoreBadges } from "./lib/store-badges-dom";
import { apiBaseOrNull, storeHref } from "./lib/store-link";

/**
 * The trade pages (/para/<slug>, and /tecnicos, the ads' page). Plain HTML
 * that reads in full without this script; the script adds what needs the
 * visit's own link: the campaign kept for a sign-up that may come days later
 * and carried through every door, this phone's store badges (store first on a
 * phone), each screenshot once its file exists and, on a page that asks for
 * it (`<body data-counts-visits>`), the two anonymous counts. No cookie, no
 * third party: it talks only to this app's API.
 */

// A visit that never clicks a door still keeps its origin: the app's own
// entry does not run on this page, so it is kept here.
captureOrigin();
// An affiliate's link may point here too: keep the code for the sign-up.
captureAffiliateCode();

/** `base` plus the tags, with the right separator: `base` may or may not carry a query already. */
const withTags = (base: string, tags: string): string => {
  if (tags === "") return base;
  return `${base}${base.includes("?") ? "&" : "?"}${tags}`;
};

/**
 * One anonymous count. A beacon, so it survives the navigation «Empieza
 * gratis aquí» starts; a POST with no body, so it crosses to the API without
 * a preflight. Never waits and never fails the page.
 */
function count(api: string, event: "visit" | "start", tags: string): void {
  const url = withTags(`${api}/acquisition/visit?event=${event}`, tags);
  try {
    if (navigator.sendBeacon(url)) return;
  } catch {
    // Refused by the browser: fall through to a fetch that outlives the page.
  }
  void fetch(url, { method: "POST", keepalive: true, mode: "no-cors" }).catch(() => undefined);
}

const tags = utmQuery(utmFromQuery(window.location.search));
const api = apiBaseOrNull();
// The server counts per day, event and campaign, not per page: only the ads'
// page counts, or every page would mix into the funnel the ads are judged by.
const counts = document.body.dataset["countsVisits"] !== undefined && api !== null;

document.querySelectorAll<HTMLAnchorElement>("a[data-start]").forEach((start) => {
  start.href = withTags(start.getAttribute("href") ?? "/auth/sign-up", tags);
  if (!counts) return;
  // A tap, a click and a ctrl- or cmd-click are a `click`; a middle click,
  // which opens a new tab, is only an `auxclick` with the middle button.
  start.addEventListener("click", () => count(api, "start", tags));
  start.addEventListener("auxclick", (event) => {
    if (event.button === 1) count(api, "start", tags);
  });
});
if (counts) count(api, "visit", tags);

// On the page that counts, the badges carry the trade and the campaign, so a
// store install is counted like a sign-up. Without an API, they link to the listing.
const trade = /[?&]type=([a-z-]+)/.exec(document.querySelector("a[data-start]")?.getAttribute("href") ?? "")?.[1];
const storeTags = [trade === undefined ? "" : `type=${trade}`, tags].filter((part) => part !== "").join("&");
const platform = detectPlatform(navigator.userAgent, navigator.maxTouchPoints);
// Only the ads' page counts a store tap: on the others it would pile taps on
// campaigns that have no visits. They link straight to the listing.
revealStoreBadges(document, platform, (store) => storeHref(counts ? api : null, store, storeTags));
// ON A PHONE, ITS STORE FIRST and the web as a link below (para.css): the
// first week of the ads, 15 «Empieza gratis aquí» opened no account and 11
// store taps brought the Android installs.
if (platform !== "desktop") document.documentElement.dataset["platform"] = platform;

// A screenshot appears once its file loads: a missing one stays hidden
// instead of drawing a broken image.
document.querySelectorAll<HTMLElement>("[data-shot]").forEach((shot) => {
  const src = shot.dataset["shot"];
  if (src === undefined || src === "") return;
  const image = new Image();
  image.alt = shot.dataset["alt"] ?? "";
  image.decoding = "async";
  image.addEventListener("load", () => {
    shot.append(image);
    shot.hidden = false;
  });
  image.src = src;
});
